import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useHookstate } from "@hookstate/core";
import {
    SITEID_GLOBAL_STATE
} from "globalStore/globalStore";
import { useTranslation } from "react-i18next";
import { PEOPLE_SERVICES } from "redux/reduxToolkitServices/_commonServices/peopleServices";
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import '@progress/kendo-theme-default/dist/all.css';
import {
    Header,
    GridContainer,
    FilterGroupContainer,
    LeftSpace,
    ButtonParent,
} from "pages/People/PeopleStyled";
import PersonIcon from "assets/svgIcons/PeopleSubSideNav/PersonIcon";
import EyeIcon from "assets/svgIcons/PeopleSubSideNav/EyeIcon";
import EyeSlashIcon from "assets/svgIcons/PeopleSubSideNav/EyeSlashIcon";
import RfidIcon from "assets/svgIcons/PeopleSubSideNav/RfidIcon";
import RfidIconSlash from "assets/svgIcons/PeopleSubSideNav/RfidIconSlash";
import CardIcon from "assets/svgIcons/PeopleSubSideNav/CardIcon";
import KeyIcon from "assets/svgIcons/PeopleSubSideNav/KeyIcon";
import ImportIcon from "assets/svgIcons/PeopleSubSideNav/ImportIcon";
import CarSlashIcon from "assets/svgIcons/PeopleSubSideNav/CarSlashIcon";
import PlusIcon from "assets/svgIcons/PeopleSubSideNav/PlusIcon";
import CloseIcon from "assets/svgIcons/CloseIcon";
import { Tooltip } from "@mui/material";
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import CarIcon from "assets/svgIcons/PeopleSubSideNav/CarIcon";
import { process } from '@progress/kendo-data-query';
import AddPersonModal from "./addPersonModal";
import EditPersonModal from "./editPersonModal";
import EditPermissionModal from "./editPermissionModal";
import AddRfidTagModal from "./addRfidTagModal";
import { isUserAdmin, isUserSupport, featureKey } from "constant";
import PeopleAlertModal from "../peopleAlertModal";

const PAGE_SIZE_KEY = 'peopleGridPageSize';

const PAGE_SIZE_OPTIONS = [15, 20, 25, 30, 40, 50, 100, 150, 200, 250];

const PeoplePage = React.memo(() => {
    const { t } = useTranslation();
     const navigate = useNavigate();

    const dispatch = useDispatch();

    const {
        getPeopleData,
        getGroupsData,
        savePersonData,
        getRfidAccessLevelData,
        editPersonData,
        getRFIDByTagData,
        transferRfidData,
        assignNewRfidData,
        getAllPeopleData,
        importPeopleData
        } = useSelector((state) => state.peopleReducers) || [];

    const { getUserRole, siteSettingsData } = useSelector((state) => state.commonReducers) || [];

    const { siteId } = useHookstate(SITEID_GLOBAL_STATE);

    const getInitialPageSize = () => {
        const saved = localStorage.getItem(PAGE_SIZE_KEY);
        return saved === 'All' ? peopleData.length : parseInt(saved) || 25;
    };

    const fileInputRef = useRef(null);

    const [peopleData, setPeopleData] = useState([]);
    const [showInactivePeople, setShowInactivePeople] = useState(false);
    const [showPeopleWithTag, setShowPeopleWithTag] = useState(true);
    const [showPeopleWithoutTag, setShowPeopleWithoutTag] = useState(true);
    const [showPeopleDriving, setShowPeopleDriving] = useState(true);
    const [showPeopleNotDriving, setShowPeopleNotDriving] = useState(true);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [groupsFilterData, setGroupsFilterData] = useState([]);
    const [groupsData, setGroupsData] = useState([]);
    const [rfidAccessLevelData, setRfidAccessLevelData] = useState([]);
    const [filter, setFilter] = useState("");
    const [addPersonModalOpen, setAddPersonModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [editPersonModalOpen, setEditPersonModalOpen] = useState(false);
    const [addRfidModalOpen, setAddRfidModalOpen] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [dataState, setDataState] = useState({ skip: 0, take: getInitialPageSize(), sort: [], filter: null });
    const [editPermissionModalOpen, setEditPermissionModalOpen] = useState(false);
    const [selectedPermission, setSelectedPermission] = useState(null);
    const [editPermissionClicked, setEditPermissionClicked] = useState(false);
    const [allPeople, setAllPeople] = useState([]);
    const [openPeopleAlert, setOpenPeopleAlert] = useState(false);
    const [titlePeopleAlert, setTitlePeopleAlert] = useState("");
    const [contentPeopleAlert, setContentPeopleAlert] = useState("");

    useEffect(() => {
        if(Object.keys(siteSettingsData).length > 0) {
            if(!siteSettingsData?.Features?.includes(featureKey.resortEdition)) {
                navigate("/dashboard", { replace: true });
            }

            dispatch(PEOPLE_SERVICES.getAllPeopleService());
            dispatch(PEOPLE_SERVICES.getPeopleBySiteIdService(siteId.get()));
            dispatch(PEOPLE_SERVICES.getGroupsBySiteIdService(siteId.get()));
            dispatch(PEOPLE_SERVICES.getRfidAccessLevelsService());
        }
    }, [siteId.get(), siteSettingsData, savePersonData, editPersonData, transferRfidData, assignNewRfidData, importPeopleData]);

    useEffect(() => {
        if(getAllPeopleData) {
            setAllPeople(getAllPeopleData);
        }
    }, [getAllPeopleData]);

    useEffect(() => {
        if(getPeopleData) {
            let filteredData = filterPeopleData();

            setPeopleData(filteredData);
            setIsLoading(false);
        }
    }, [getPeopleData]);

    useEffect(() => {
        if(getGroupsData) {
            const initialGroup = { GroupId: 0, GroupName: "All" };
            const updatedGroupList = [initialGroup, ...getGroupsData];

            setGroupsFilterData(updatedGroupList);
            setGroupsData(getGroupsData);
        }
    }, [getGroupsData]);

    useEffect(() => {
        if(getRfidAccessLevelData) {
            setRfidAccessLevelData(getRfidAccessLevelData);
        }
    }, [getRfidAccessLevelData]);

    useEffect(() => {
        if(getRFIDByTagData && editPermissionClicked) {
            setSelectedPermission(getRFIDByTagData);
            setEditPermissionModalOpen(true);
            setEditPermissionClicked(false);
        }        
    }, [getRFIDByTagData]);

    useEffect(() => {
        const pageSize = dataState.take === peopleData.length ? 'All' : dataState.take;
        localStorage.setItem(PAGE_SIZE_KEY, pageSize);
    }, [dataState.take, peopleData.length]);

    const handleShowInactivePeople = () => {
        let filteredPeopleData = getPeopleData;

        if(selectedGroups.length > 0) {
            var groupNames = selectedGroups.map(x => x.GroupName);
            filteredPeopleData = filteredPeopleData.filter(p => groupNames.includes(p.GroupName));
        }

        let shouldShowInactivePeople = !showInactivePeople;

        setShowInactivePeople(shouldShowInactivePeople);

        if(shouldShowInactivePeople) {           
            setPeopleData(filteredPeopleData);
        } else {
            var activePeople = filteredPeopleData.filter(x => !x.Inactivated);
            setPeopleData(activePeople);
        }
    }

    const filterInput = (e) => {
        let filteredPeopleData = filterPeopleData();

        setFilter(e.target.value);

        if(e.target.value == "") {
            setPeopleData(filteredPeopleData);
        } else {
            var valueInput = e.target.value.toLowerCase();

            var filteredData = filteredPeopleData.filter(p =>
                (p.LastName && p.LastName.toLowerCase().includes(valueInput)) ||
                (p.FirstName && p.FirstName.toLowerCase().includes(valueInput)) ||
                (p.Identifier && p.Identifier.toLowerCase().includes(valueInput)) ||
                (p.RFIDAccessLevel && p.RFIDAccessLevel.toLowerCase().includes(valueInput)) ||
                (p.PersonId && p.PersonId.toString().toLowerCase().includes(valueInput)) ||
                (p.RFIDTag && p.RFIDTag.toLowerCase().includes(valueInput)));
    
            setPeopleData(filteredData);
        }
    }

    const handleGroup = (e) => {
        let filteredPeopleData = getPeopleData;
        if(!showInactivePeople) {
            filteredPeopleData = filteredPeopleData.filter(x => !x.Inactivated);
        }

        setSelectedGroups(e.target.value);

        var groupNames = e.target.value.map(x => x.GroupName);

        if(groupNames.length == 0 || (groupNames.length > 0 && groupNames.includes("All"))) {
            setPeopleData(filteredPeopleData);
        } else {
            var filteredData = filteredPeopleData.filter(p => groupNames.includes(p.GroupName));
            setPeopleData(filteredData);
        }
    }

    const handleClearNameFilter = () => {
        setSelectedGroups([]);
        setFilter("");
        setPeopleData(getPeopleData);
    }

    const handlePeopleWithTag = () => {
        let value = !showPeopleWithTag;
        setShowPeopleWithTag(value);

        let filteredData = filterPeopleData();
        
        if(value) {            
            setPeopleData(filteredData);
        } else {
            var peopleWithTag = filteredData.filter(x => x.RFIDTag == null);
            setPeopleData(peopleWithTag);    
            
            if(!showPeopleWithoutTag)
                setShowPeopleWithoutTag(true);
        }        
    }

    const handlePeopleWithoutTag = () => {
        let value = !showPeopleWithoutTag;
        setShowPeopleWithoutTag(value);

        let filteredData = filterPeopleData();
        
        if(value) {            
            setPeopleData(filteredData);
        } else {
            var peopleWithTag = filteredData.filter(x => x.RFIDTag != null);
            setPeopleData(peopleWithTag);

            if(!showPeopleWithTag)
                setShowPeopleWithTag(true);
        }
    }

    const handlePeopleDriving = () => {
        let value = !showPeopleDriving;
        setShowPeopleDriving(value);
        
        let filteredData = filterPeopleData();

        if(value) {            
            setPeopleData(filteredData);
        } else {
            var peopleDriving = filteredData.filter(x => x.IsPersonDriving);
            setPeopleData(peopleDriving);

            if(!showPeopleNotDriving)
                setShowPeopleNotDriving(true);
        }
    }

    const handlePeopleNotDriving = () => {
        let value = !showPeopleNotDriving;
        setShowPeopleNotDriving(value);
        
        let filteredData = filterPeopleData();

        if(value) {            
            setPeopleData(filteredData);
        } else {
            var peopleNotDriving = filteredData.filter(x => !x.IsPersonDriving);
            setPeopleData(peopleNotDriving);

            if(!showPeopleDriving)
                setShowPeopleDriving(true);
        }
    }

    const handleEditPersonClick = (person) => {
        setSelectedPerson(person);
        setEditPersonModalOpen(true);
    }

    const handleEditPermissionClick = (person) => {
        setEditPermissionClicked(true);
        dispatch(PEOPLE_SERVICES.getRFIDByTagService(siteId.get(), person.RFIDTag));
    }

    const handleAddRfidTagClick = (person) => {
        setSelectedPerson(person);
        setAddRfidModalOpen(true);
    }

    const filterPeopleData = () => {
        let filteredPeopleData = getPeopleData;
        if(!showInactivePeople) {
            filteredPeopleData = filteredPeopleData.filter(x => !x.Inactivated);
        }

        if(selectedGroups.length > 0) {
            var groupNames = selectedGroups.map(x => x.GroupName);
            filteredPeopleData = filteredPeopleData.filter(p => groupNames.includes(p.GroupName));
        }

        return filteredPeopleData;
    }

    const actionsCell = (props) => {
        return (
                <td>
                    <button
                        title={t("Edit_person")}
                        className="k-button"
                        style={{ marginRight: 10 }}
                        onClick={() => handleEditPersonClick(props.dataItem)}
                    >
                        <CardIcon color={"#000"} />
                    </button>
                    <button
                        title={t("Add_Rfid")}
                        className="k-button"
                        style={{ marginRight: 10 }}
                        onClick={() => handleAddRfidTagClick(props.dataItem)}
                    >
                        <RfidIcon color={"#000"} />
                    </button>
                    {showEditPermissionButton &&
                        <button
                            title={t("Edit_Permission")}
                            className="k-button"
                            onClick={() => handleEditPermissionClick(props.dataItem)}
                            disabled={props.dataItem.RFIDTag == null}
                        >
                            <KeyIcon color={"#000"} />
                        </button>
                    }
                </td>
        );
    };

    const InactivatedCell = (props) => {
        const { dataItem } = props;
    
        return (
            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                {dataItem.Inactivated ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                    }}>
                        <CloseIcon />
                    </div>
                ) : null}
            </td>
        );
    };

    const handleDataStateChange = (event) => {
        let { take, skip } = event.dataState;

        if (take === 'All') {
            take = peopleData.length;
            skip = 0;
        }

        setDataState({
        ...event.dataState,
            take,
            skip,
        });
    };

    const processedData = process(peopleData ?? [], dataState);

    const showEditPermissionButton = () => {
        return isUserAdmin(getUserRole && getUserRole?.Role) || isUserSupport(getUserRole && getUserRole?.Role)
    }

    const importPeople = () => {
        fileInputRef.current?.click();
    };

    const handleImportPeople = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
        const csvData = e.target.result;
            dispatch(PEOPLE_SERVICES.importPeopleService(siteId.get(), csvData)).catch((ex) => {
                setTitlePeopleAlert(t("Message"));
                setContentPeopleAlert(t("Import_People_Error"));
                setOpenPeopleAlert(true);
            });
        };

        reader.readAsText(file);
    };

    return (
        <>
            <Header>
                <LeftSpace>
                    <Tooltip title={t("Logged_into_vehicle")}>
                        <button onClick={handlePeopleDriving} style={{padding: "2px 5px", backgroundColor: showPeopleDriving ? "var(--theme-background)" : "transparent", border: `2px solid #fff`}}>
                            <CarIcon color="#fff" />
                        </button>
                    </Tooltip>
                
                    <Tooltip title={t("Not_Logged_into_vehicle")}>
                        <button onClick={handlePeopleNotDriving} style={{padding: "2px 5px", backgroundColor: showPeopleNotDriving ? "var(--theme-background)" : "transparent", border: `2px solid #fff`}}>
                            <CarSlashIcon color="#fff" />
                        </button>
                    </Tooltip>
                </LeftSpace>

                <LeftSpace>             
                    <Tooltip title={t("People_with_rfid")}>
                        <button onClick={handlePeopleWithTag} style={{padding: "2px 5px", backgroundColor: showPeopleWithTag ? "var(--theme-background)" : "transparent", border: `2px solid #fff`}}>
                            <RfidIcon color="#fff" />
                        </button>
                    </Tooltip>

                    <Tooltip title={t("People_without_rfid")}>
                        <button onClick={handlePeopleWithoutTag} style={{padding: "2px 5px", backgroundColor: showPeopleWithoutTag ? "var(--theme-background)" : "transparent", border: `2px solid #fff`}}>
                            <RfidIconSlash color="#fff" />
                        </button>
                    </Tooltip>
                </LeftSpace>

                <LeftSpace>
                    <Tooltip title={t("Show_Inactive_People")}>
                        <button onClick={handleShowInactivePeople} style={{padding: "2px 5px", backgroundColor: showInactivePeople ? "var(--theme-background)" : "transparent", border: `2px solid #fff`}}>
                            <PersonIcon width="16" height="16" color="#fff" /> <span style={{color: "#fff"}}>/</span>
                        </button>
                    </Tooltip>
                </LeftSpace>

                <FilterGroupContainer>
                    <MultiSelect
                        data={groupsFilterData}
                        textField="GroupName"
                        dataItemKey="GroupId"
                        value={selectedGroups}
                        onChange={(e) => handleGroup(e)}
                        placeholder="Select Groups"
                        style={{ height: 30 }}
                    />                
                </FilterGroupContainer>

                <LeftSpace>
                    <input type="text" value={filter} placeholder="Filter" onChange={(e) => filterInput(e)} style={{ width: 300, height: 30, border: 'solid 1px #e7e7e7'}}  />
                </LeftSpace>

                <ButtonParent>
                    <Tooltip title={t("Clear_Name_Filter")}>
                        <button onClick={handleClearNameFilter} style={{backgroundColor: "transparent", border: "3px solid #fff"}}>
                            <CloseIcon width={19} height={19} color="#fff" /> 
                        </button>
                    </Tooltip>
                </ButtonParent>

                <LeftSpace>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 3, color: "#fff" }}>
                        <span title={t("Active_People_Count")} >
                            <EyeIcon style={{ verticalAlign: 'middle' }} color="#fff" />
                            {peopleData.length}
                        </span>
                        <span title={t("Inactive_People_Count")}>
                            <EyeSlashIcon style={{ verticalAlign: 'middle' }} color="#fff" />
                            {getPeopleData.length - peopleData.length}
                        </span>
                    </div>
                </LeftSpace>

                <LeftSpace>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Tooltip title={t("Import")}>
                                <button
                                    onClick={importPeople}
                                    style={{
                                        backgroundColor: "var(--theme-background)",
                                        padding: 5,
                                        border: "1px solid #fff"
                                    }}
                                >
                                    <ImportIcon style={{ verticalAlign: 'middle' }} color={"#fff"} width={16} height={16} />
                                    <span style={{ verticalAlign: 'middle', color: "#fff", fontWeight: "bold" }}>
                                        {t("Import")}
                                    </span>
                                </button>
                                <input type="file" ref={fileInputRef} id="fileInput" onChange={handleImportPeople} style={{display: 'none'}} accept=".csv" />
                            </Tooltip>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Tooltip title={t("Add")}>
                                <button
                                    onClick={() => setAddPersonModalOpen(true)}
                                    style={{
                                        backgroundColor: "var(--theme-background)",
                                        padding: 5,
                                        border: "1px solid #fff"
                                    }}
                                >
                                    <PlusIcon style={{ verticalAlign: 'middle' }} color={"#fff"} width={16} height={16} />
                                    <span style={{ verticalAlign: 'middle', color: "#fff", fontWeight: "bold" }}>
                                        {t("Add")}
                                    </span>
                                </button>
                            </Tooltip>
                        </div>
                    </div>
                </LeftSpace>
            </Header>

            <GridContainer>
                <Grid
                    data={processedData}
                    pageable={{
                        buttonCount: 5,
                        pageSizes: ['All', ...PAGE_SIZE_OPTIONS],
                        pageSizeValue: dataState.take === peopleData.length ? 'All' : dataState.take,
                    }}
                    sortable
                    filterable
                    loading={isLoading}
                    {...dataState}
                    onDataStateChange={handleDataStateChange}
                    total={peopleData.length}
                >
                    <Column title="" cell={actionsCell} filterable={false} />
                    <Column field="LastName" title={t("Last_Name")} />
                    <Column field="FirstName" title={t("First_Name")} />
                    <Column field="Identifier" title={t("ID2")} />
                    <Column field="GroupName" title={t("Group")} />
                    <Column field="RFIDAccessLevel" title={t("Rfid_Access_Level")} />
                    <Column field="RFIDTag" title={t("Rfid_Tag")} />
                    <Column field="Inactivated" title={t("Off")} cell={InactivatedCell} filter="boolean" />
                    <Column field="PersonId" title={t("Person_Id")} />
                </Grid>
            </GridContainer>

            <AddRfidTagModal setIsLoading={setIsLoading}
                             selectedPerson={selectedPerson}
                             addRfidModalOpen={addRfidModalOpen}
                             setAddRfidModalOpen={setAddRfidModalOpen}
                             allPeople={allPeople} />

            <EditPermissionModal setIsLoading={setIsLoading}
                                 selectedPermission={selectedPermission}
                                 setSelectedPermission={setSelectedPermission}
                                 editPermissionModalOpen={editPermissionModalOpen}
                                 setEditPermissionModalOpen={setEditPermissionModalOpen} />

           <EditPersonModal groupsData={groupsData}
                            rfidAccessLevelData={rfidAccessLevelData}
                            setIsLoading={setIsLoading}
                            selectedPerson={selectedPerson}
                            setSelectedPerson={setSelectedPerson}
                            editPersonModalOpen={editPersonModalOpen}
                            setEditPersonModalOpen={setEditPersonModalOpen} />

            <AddPersonModal groupsData={groupsData}
                            rfidAccessLevelData={rfidAccessLevelData}
                            setIsLoading={setIsLoading}
                            addPersonModalOpen={addPersonModalOpen}
                            setAddPersonModalOpen={setAddPersonModalOpen} />
            <PeopleAlertModal
                                 openPeopleAlertModalOpen={openPeopleAlert} 
                                 yesCallbackFunction={() => {}}
                                 noCallbackFunction={() => setOpenPeopleAlert(false)}
                                 title={titlePeopleAlert}
                                 content={contentPeopleAlert} 
                                 loading={false}
                                 isMessageOnly={true} />
        </>
    );
});

export default PeoplePage;
